import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  MaterialSlot,
  MaterialSlotCreate,
  MaterialSlotUpdate,
} from "@/api/schemas";

/** Pridaj slot do variant (auto-derivuje hrany z dekoru materiálu). */
export function useCreateSlot(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<DataResponse<MaterialSlot>, Error, MaterialSlotCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MaterialSlotCreate) => {
      const { data } = await api.post<DataResponse<MaterialSlot>>(
        `/varianty/${variantaId}/sloty`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
    },
  });
}

/** PATCH slotu — zmena material_id triggeruje bulk recalc všetkých dielcov. */
export function useUpdateSlot(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<MaterialSlot>,
  Error,
  { id: string; data: MaterialSlotUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<MaterialSlot>>(
        `/sloty/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      // Zmena materiálu slotu mení kandidátne hrany + sumár hrán
      qc.invalidateQueries({ queryKey: queryKeys.slotEdges.all });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() });
    },
  });
}

/** Tvrdý delete slotu — 409 ak je v dielcoch. */
export function useDeleteSlot(
  zakazkaId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sloty/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
    },
  });
}
